import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { sessionEvents } from '@/lib/session-events';

/**
 * Warning threshold before session expiry (5 minutes)
 */
const EXPIRY_WARNING_MS = 5 * 60 * 1000;

/**
 * Check interval for session expiry (1 minute)
 */
const EXPIRY_CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Operator metadata from JWT app_metadata
 */
export interface OperatorMetadata {
  tenant_id: string;
  operator_id: string;
  roles?: string[];
}

/**
 * Auth context value
 */
interface AuthContextValue {
  session: Session | null;
  user: User | null;
  operatorMetadata: OperatorMetadata | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Get expiry timestamp from session
 */
function getSessionExpiryMs(session: Session | null): number | null {
  if (!session?.expires_at) return null;
  return session.expires_at * 1000; // Convert to milliseconds
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refs to track expiry warning state
  const expiryWarningShownRef = useRef(false);
  const expiryCheckIntervalRef = useRef<number | null>(null);
  const warningToastIdRef = useRef<string | number | null>(null);

  /**
   * Handle session expiry and sign out
   */
  const handleSessionExpired = useCallback(async () => {
    console.warn('[Auth] Session expired, signing out');
    toast.error('Your session has expired. Please sign in again.');
    await supabase.auth.signOut();
  }, []);

  /**
   * Check session expiry and show warning if needed
   */
  const checkSessionExpiry = useCallback(() => {
    if (!session) return;

    const expiryMs = getSessionExpiryMs(session);
    if (!expiryMs) return;

    const now = Date.now();
    const timeUntilExpiry = expiryMs - now;

    // Session already expired
    if (timeUntilExpiry <= 0) {
      handleSessionExpired();
      return;
    }

    // Show warning if within threshold and not already shown
    if (timeUntilExpiry <= EXPIRY_WARNING_MS && !expiryWarningShownRef.current) {
      expiryWarningShownRef.current = true;
      const minutesLeft = Math.ceil(timeUntilExpiry / 60000);
      warningToastIdRef.current = toast.warning(
        `Your session will expire in ${minutesLeft} minute${minutesLeft !== 1 ? 's' : ''}. Please save your work.`,
        {
          duration: 30000, // Show for 30 seconds
          id: 'session-expiry-warning',
        }
      );
    }
  }, [session, handleSessionExpired]);

  /**
   * Reset expiry warning state (called when session refreshes)
   */
  const resetExpiryWarning = useCallback(() => {
    expiryWarningShownRef.current = false;
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current);
      warningToastIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);

      // Reset expiry warning on token refresh
      if (event === 'TOKEN_REFRESHED') {
        console.info('[Auth] Token refreshed');
        resetExpiryWarning();
      }

      // Clear warnings on sign out
      if (event === 'SIGNED_OUT') {
        resetExpiryWarning();
      }
    });

    return () => subscription.unsubscribe();
  }, [resetExpiryWarning]);

  // Set up session expiry checking
  useEffect(() => {
    if (!session) {
      // Clear interval when no session
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current);
        expiryCheckIntervalRef.current = null;
      }
      return;
    }

    // Check immediately
    checkSessionExpiry();

    // Set up periodic check
    expiryCheckIntervalRef.current = window.setInterval(
      checkSessionExpiry,
      EXPIRY_CHECK_INTERVAL_MS
    );

    return () => {
      if (expiryCheckIntervalRef.current) {
        clearInterval(expiryCheckIntervalRef.current);
        expiryCheckIntervalRef.current = null;
      }
    };
  }, [session, checkSessionExpiry]);

  // Listen for session events from API client
  useEffect(() => {
    const unsubExpired = sessionEvents.on('session:expired', () => {
      handleSessionExpired();
    });

    const unsubRefreshFailed = sessionEvents.on('session:refresh_failed', () => {
      console.warn('[Auth] Session refresh failed');
      handleSessionExpired();
    });

    return () => {
      unsubExpired();
      unsubRefreshFailed();
    };
  }, [handleSessionExpired]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  // Extract operator metadata from JWT app_metadata
  const operatorMetadata: OperatorMetadata | null = (() => {
    const appMeta = session?.user?.app_metadata;
    if (!appMeta?.tenant_id || !appMeta?.operator_id) return null;
    const result: OperatorMetadata = {
      tenant_id: appMeta.tenant_id as string,
      operator_id: appMeta.operator_id as string,
    };
    if (Array.isArray(appMeta.roles)) result.roles = appMeta.roles;
    return result;
  })();

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        operatorMetadata,
        isLoading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
